import { saveAs } from "file-saver";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Button } from "reactstrap";

import { LoadingSpinner } from "../../../../constants/const";
import { encryptStorage } from "../../../../constants/EncryptStorage";
import { ApiFinance } from "../../../funds/ApiFinance";
import { GetLanguageString } from "../../../helper/Components";
import { GetSelectedLanguage } from "../../../helper/Method";
import { ShowToast } from "../../../shared/ShowToast";
import {
  fileNameFromDisposition,
  parseFundsReportBlobError,
} from "../shared/fundsReportDownload";
import CashBalanceReportExportButtons from "./CashBalanceReportExportButtons";
import CashBalanceReportFilters from "./CashBalanceReportFilters";
import CashBalanceReportSummary from "./CashBalanceReportSummary";
import CashBalanceReportTable from "./CashBalanceReportTable";

const getInitialValues = () => {
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");
  return {
    unit: {
      value: loggedInUnitId,
      label: "",
      label1: unitTypeName,
    },
    excludeExUnit: true,
    showSelectedUnitsCheck: "None",
    unitsExceedingLimits: false,
  };
};

const CashBalanceReport = ({ history }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(getInitialValues);
  const [loading, setLoading] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState({
    rows: [],
    limits: null,
    totalUnits: 0,
    exceedingCount: 0,
  });

  const buildPayload = (searchValues = values) => ({
    unitId: searchValues.unit.value,
    unitName: searchValues.unit.label || searchValues.unit.lable,
    showSelectedUnitsCheck: searchValues.showSelectedUnitsCheck || "None",
    unitsExceedingLimits: !!searchValues.unitsExceedingLimits,
    language: GetSelectedLanguage(),
  });

  const handleSearch = (searchValues) => {
    if (loading) return;

    setSearchTriggered(true);
    setLoading(true);

    ApiFinance()
      .GetCashBalanceReport(buildPayload(searchValues))
      .then((res) => {
        setReport({
          rows: res.data?.rows || [],
          limits: res.data?.limits || null,
          totalUnits: res.data?.totalUnits || 0,
          exceedingCount: res.data?.exceedingCount || 0,
        });
        setLoading(false);
      })
      .catch((err) => {
        const messageKey = err.response?.data?.message;
        toast.error(
          (messageKey && t(messageKey) !== messageKey ? t(messageKey) : null) ||
            messageKey ||
            t("cash_balance_report_load_failed")
        );
        setLoading(false);
      });
  };

  const downloadReport = (apiCall, mimeType, fallbackName, loadingKey, successKey) => {
    if (downloading) return;

    const toastId = toast.loading(t(loadingKey));
    setDownloading(true);
    apiCall(buildPayload())
      .then((res) => {
        const blob = new Blob([res.data], { type: mimeType });
        saveAs(blob, fileNameFromDisposition(res.headers, fallbackName));
        ShowToast(toastId, successKey, "success");
      })
      .catch(async (err) => {
        const messageKey = await parseFundsReportBlobError(err);
        ShowToast(
          toastId,
          (messageKey && t(messageKey) !== messageKey ? messageKey : null) ||
            "cash_balance_report_download_failed",
          "error"
        );
      })
      .finally(() => setDownloading(false));
  };

  const handleExcel = () =>
    downloadReport(
      ApiFinance().GetCashBalanceReportExcel,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "CashBalanceReport.xlsx",
      "downloading_excel",
      "downloaded_excel"
    );

  const handlePdf = () =>
    downloadReport(
      ApiFinance().GetCashBalanceReportPdf,
      "application/pdf",
      "CashBalanceReport.pdf",
      "downloading_pdf",
      "downloaded_pdf"
    );

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <ToastContainer
        autoClose={3000}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        draggable
      />
      <div
        className="ibox"
        style={{
          borderRadius: "12px",
          overflow: "visible",
          position: "relative",
          zIndex: 20,
          boxShadow: "0 6px 20px rgba(45, 68, 85, 0.08)",
        }}
      >
        <div
          className="ibox-title d-flex justify-content-between align-items-center"
          style={{
            background: "linear-gradient(135deg, #486A84 0%, #2D4455 100%)",
            color: "#fff",
            border: "none",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <h5
            className={GetSelectedLanguage() === "ur" ? "ur" : ""}
            style={{ color: "#fff", margin: 0, fontWeight: 700 }}
          >
            <GetLanguageString props="cash_balance_report_title" />
          </h5>
          <Button
            style={{
              background: "rgba(255, 255, 255, 0.16)",
              border: "1px solid rgba(255, 255, 255, 0.28)",
              color: "#ffffff",
              borderRadius: "8px",
              padding: "4px 12px",
              fontSize: "12px",
              fontWeight: "600",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.28)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.16)";
            }}
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#ffffff"
              size={15}
              style={{ marginBottom: 1 }}
            />
            <GetLanguageString props="courses_participants_back" />
          </Button>
        </div>
        <CashBalanceReportFilters
          values={values}
          setValues={setValues}
          onSubmit={handleSearch}
          loading={loading}
        />
      </div>
      {searchTriggered ? (
        loading ? (
          <LoadingSpinner />
        ) : (
          <div
            className="ibox"
            style={{
              borderRadius: "12px",
              overflow: "visible",
              position: "relative",
              zIndex: 1,
              boxShadow: "0 6px 20px rgba(45, 68, 85, 0.08)",
            }}
          >
            <div className="ibox-content">
              <div className="d-flex justify-content-end m-b-sm">
                <CashBalanceReportExportButtons
                  onExcel={handleExcel}
                  onPdf={handlePdf}
                  disabled={downloading}
                />
              </div>
              <CashBalanceReportSummary
                totalUnits={report.totalUnits}
                exceedingCount={report.exceedingCount}
                limits={report.limits}
              />
              <CashBalanceReportTable rows={report.rows} />
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};

export default withRouter(CashBalanceReport);
